import React, { FC, useState, useMemo, useEffect, useRef } from 'react'

import i18n from 'i18next'
import { AtlasModal } from 'components/atlas-components/atlas-modal/atlas-modal'
import { AtlasButtonOld } from 'components/atlas-material/button'
import {
    getUpdatedMinutesItem,
    initializeFormValues,
    getMinutesMetaData
} from './minutes-properties-form-functions'
import { DialogContent, Button, Tabs, Tab, Box, makeStyles } from '@material-ui/core'
import { css, ClassNames } from '@emotion/react'
import { EditMode } from './use-minutes-properties-modal'
import { MinutesPropertiesForm, submitMinutesPropertiesForm } from './minutes-properties-form'
import {
    AtlasActionPrimary,
    ColorBaseSkyBlue,
    ColorBaseMulberryBlue,
    ColorBaseWhite
} from 'assets/styles/variables'
import blockingOperation from 'businesslayer/blockingOperation'
import { useEditModal } from 'businesslayer/networkrequests/minutes/useEditModal'
import { MINUTES_EDIT_OPTIONS } from 'components/minutetaker/components/InvitationView/types'
import LinearIndeterminate from 'components/shared/LinearProgress'
import { getSummaryStatus } from 'businesslayer/networkrequests/book'
import { transientStorageManager } from 'businesslayer/minutesSessionStore'
import { getSessionStorageItem } from 'businesslayer/minutesLocalStore'
import MinuteManagerAttendees from 'components/minutemanager/dialog/MinuteManagerAttendees'
import TextLengthCounter from 'components/shared/TextLengthCounter'
import { InputLabelStyled } from 'components/minutetaker/components/form/AssigneeChipInputField'
import { NewDialogTextField } from 'components/shared/StyledComponents'
import { pendoTrack } from 'common/util/functions'

const TextFieldStyles = css`
    &.MuiInputBase-root.MuiOutlinedInput-root,
    & .MuiInputBase-root.MuiOutlinedInput-root,
    .MuiInputBase-root.MuiOutlinedInput-root,
    div[class*='MuiOutlinedInput-root'] {
        margin-top: 5px !important;
        padding: 0 !important;
    }
    .MuiInputBase-root .MuiOutlinedInput-input,
    .MuiInputBase-root textarea.MuiOutlinedInput-input,
    textarea[class*='MuiOutlinedInput-input'] {
        text-indent: 0 !important;
        padding: 10px 16px !important;
        overflow: hidden !important;
        resize: none !important;
        min-height: 50px !important;
    }
    .MuiOutlinedInput-multiline,
    div[class*='MuiOutlinedInput-multiline'] {
        padding: 0 !important;
    }
    .MuiOutlinedInput-notchedOutline,
    fieldset[class*='MuiOutlinedInput-notchedOutline'] {
        border-width: 1px !important;
    }
    .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline,
    .MuiInputBase-root.Mui-focused fieldset[class*='MuiOutlinedInput-notchedOutline'] {
        border-width: 1px !important;
        box-shadow: 0px 0px 0px 2px ${ColorBaseSkyBlue} !important;
    }
    p {
        margin-left: 0;
        margin-right: 0;
    }
`

const useTabStyles = makeStyles({
    tab: {
        textTransform: 'none',
        fontSize: '16px',
        fontWeight: 600,
        '&:hover': {
            backgroundColor: '#94949429',
            borderRadius: '2px'
        },
        '&:focus': {
            outline: 'none',
            boxShadow: `0px 0px 0px 3px ${ColorBaseWhite}, 0px 0px 0px 2px ${ColorBaseMulberryBlue} inset`,
            borderRadius: '2px'
        }
    }
})

type Props = {
    isOpen: boolean
    editMode: EditMode
    minutesItem: Partial<MinutesUnnormalized> | undefined
    title: string
    onCloseMinutesEditor: () => void
    onSaveMinutesItem: ({
        updatedMinutesItem,
        logoFile
    }: {
        updatedMinutesItem: MinutesUnnormalized
        logoFile?: File
    }) => void
    bookId: string
}

export const MinutesPropertiesModal: FC<Props> = ({
    minutesItem,
    isOpen,
    onCloseMinutesEditor,
    onSaveMinutesItem,
    title,
    editMode,
    bookId
}) => {
    const [canSaveMinutes, setCanSaveMinutes] = useState(false)
    const [activeTab, setActiveTab] = useState(0)
    const [attendanceNotes, setAttendanceNotes] = useState('')
    const hasTrackedAttendanceNotesRef = useRef(false)
    const getAttendeesDataRef = React.useRef<(() => any) | null>(null)
    const tabClasses = useTabStyles()

    const handleRegisterGetData = React.useCallback((getter: () => any) => {
        getAttendeesDataRef.current = getter
    }, [])

    const { editModalData, editMinutesDetails } = useEditModal(
        minutesItem?.id,
        minutesItem?.committee_id || minutesItem?.committee?.id,
        editMode
    )

    const [selectedCommitteeId, setSelectedCommitteeId] = useState<string | null>(
        editModalData?.committeeId ||
            minutesItem?.committee_id ||
            minutesItem?.committee?.id ||
            null
    )

    let obj = { ...minutesItem }
    if (editModalData.committeeId) {
        obj = {
            ...minutesItem,
            ...editModalData
        }
    }
    const [showSummaryStatus, setShowSummaryStatus] = useState<{ status: string }>({ status: '' })

    const committeeName = transientStorageManager.committeeName
    const currCommitteeId = getSessionStorageItem('currentCommitteeId')
    const minutesItemId = minutesItem?.id

    useEffect(() => {
        const callAPI = async () => {
            if (bookId && currCommitteeId) {
                const status = await getSummaryStatus(bookId, currCommitteeId, committeeName)
                setShowSummaryStatus(status)
            }
        }
        callAPI()
    }, [bookId, currCommitteeId, committeeName])

    // Initialize attendance notes for EDIT/DUPLICATE mode
    useEffect(() => {
        if (
            (editMode === MINUTES_EDIT_OPTIONS.EDIT ||
                editMode === MINUTES_EDIT_OPTIONS.DUPLICATE) &&
            editModalData?.notes
        ) {
            setAttendanceNotes(editModalData.notes)
        }
    }, [editMode, editModalData])

    useEffect(() => {
        if (isOpen) {
            hasTrackedAttendanceNotesRef.current = false
        }
    }, [isOpen, editMode, minutesItemId])

    const initialFormValues = useMemo(
        () =>
            initializeFormValues(
                { ...minutesItem, ...editModalData, editoMode: editMode },
                editMode
            ),
        [minutesItem, editMode, editModalData]
    )

    const handleClickSave = () => {
        if (canSaveMinutes) {
            // Submit logic for final-form
            submitMinutesPropertiesForm()
        }
    }
    const handleSave = (values, logoFile?: File) => {
        blockingOperation.showProgress(null, true)
        if (!canSaveMinutes) return
        const updatedMinutesItem = getUpdatedMinutesItem(minutesItem, values)

        if (getAttendeesDataRef.current) {
            const attendeesData = getAttendeesDataRef.current()
            updatedMinutesItem.presentAttendees = attendeesData.presentAttendees
            updatedMinutesItem.invitedAttendees = attendeesData.invitedAttendees
            updatedMinutesItem.absentAttendees = attendeesData.absentAttendees
        }

        // Add attendance notes to the payload
        updatedMinutesItem.notes = attendanceNotes

        onSaveMinutesItem({ updatedMinutesItem, logoFile })
        return false
    }

    const renderModalActionButtons = (
        okButtonLabel: string,
        saveButtonDisabled: boolean
    ): Array<any> => {
        const cancelButton = (
            <ClassNames key={'DialogActionCancel'}>
                {({ css }) => (
                    <Button
                        key={'DialogActionCancel'}
                        className={css`
                            ${btnCancel}
                        `}
                        onClick={onCloseMinutesEditor}
                        disableFocusRipple
                        disableRipple
                        data-testid="MinutesManagerModal_CancelBtn">
                        {i18n.t('CANCEL')}
                    </Button>
                )}
            </ClassNames>
        )

        const okButton = (
            <ClassNames key={'DialogActionSave'}>
                {({ css }) => (
                    <AtlasButtonOld
                        key={'DialogActionSave'}
                        disabled={saveButtonDisabled}
                        onClick={handleClickSave}
                        className={css`
                            ${btnOk}
                        `}
                        dataTestId="MinutesManagerModal_SaveBtn">
                        {okButtonLabel}
                    </AtlasButtonOld>
                )}
            </ClassNames>
        )
        return [cancelButton, okButton]
    }

    const okButtonLabel = getOkButtonLabel(editMode)
    const dialogActions = renderModalActionButtons(okButtonLabel, !canSaveMinutes)

    const minutesMetaData =
        !!minutesItem && editMode === MINUTES_EDIT_OPTIONS.EDIT ? getMinutesMetaData(obj) : []

    const handleTabChange = (_event: React.ChangeEvent<{}>, newValue: number) => {
        setActiveTab(newValue)
        // Reset scroll position when switching tabs
        const dialogContent = document.querySelector('[role="dialog"] .MuiDialogContent-root')
        if (dialogContent) {
            dialogContent.scrollTop = 0
        }
    }

    const trackAttendanceNotesOnce = (interaction: 'change' | 'focus') => {
        if (!hasTrackedAttendanceNotesRef.current) {
            hasTrackedAttendanceNotesRef.current = true
            pendoTrack('MM-PropertiesModal-AttendanceNotes', {
                interaction,
                source: 'properties-modal',
                domEvent: interaction
            })
        }
    }

    const onAttendanceNotesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setAttendanceNotes(event.target.value)
        trackAttendanceNotesOnce('change')
    }

    const attendeesMinutesItem = useMemo(() => {
        const merged = {
            ...minutesItem,
            ...editModalData
        }

        if (!merged.id && !merged.attendees) {
            merged.attendees = []
        }

        return merged
    }, [minutesItem, editModalData])

    return (
        <AtlasModal
            open={isOpen}
            modalTitle={title}
            maxWidth="md"
            onClose={onCloseMinutesEditor}
            data-testid="MinutesManagerModal"
            PaperProps={{
                ref: (elem: Element) => {
                    !!elem && elem.setAttribute('data-testid', 'MinutesManagerModal_MUIPaper')
                }
            }}
            actions={dialogActions}>
            <LinearIndeterminate loading={editMinutesDetails} />
            <Tabs
                value={activeTab}
                onChange={handleTabChange}
                indicatorColor="primary"
                textColor="primary"
                aria-label={i18n.t('MINUTES_PROPERTIES_TAB')}>
                <Tab
                    className={tabClasses.tab}
                    label={i18n.t('MINUTES_PROPERTIES_TAB')}
                    id="tab-0"
                    aria-controls="tabpanel-0"
                    disableFocusRipple
                    disableRipple
                    tabIndex={0}
                    style={{
                        color:
                            activeTab === 0
                                ? 'var(--Semantic-Color-Action-Active, #252C44)'
                                : 'var(--Semantic-Color-Action-Default, #455D82)',
                        textAlign: 'center',
                        fontFamily: '"Source Sans Pro"',
                        fontSize: '16px',
                        fontStyle: 'normal',
                        lineHeight: '20px'
                    }}
                />
                <Tab
                    className={tabClasses.tab}
                    label={i18n.t('ATTENDANCE')}
                    id="tab-1"
                    aria-controls="tabpanel-1"
                    disableFocusRipple
                    disableRipple
                    tabIndex={0}
                    style={{
                        color:
                            activeTab === 1
                                ? 'var(--Semantic-Color-Action-Active, #252C44)'
                                : 'var(--Semantic-Color-Action-Default, #455D82)',
                        textAlign: 'center',
                        fontFeatureSettings: "'liga' off, 'clig' off",
                        fontFamily: '"Source Sans Pro"',
                        fontSize: '16px',
                        fontStyle: 'normal',
                        lineHeight: '20px'
                    }}
                />
            </Tabs>
            <DialogContent
                style={{
                    maxHeight: '60vh',
                    overflowY: 'auto',
                    position: 'relative'
                }}>
                <ClassNames>
                    {({ css }) => (
                        <div
                            className={css`
                                ${tabPanelContainer}
                            `}>
                            <TabPanel value={activeTab} index={0}>
                                <MinutesPropertiesForm
                                    minutesMetaData={minutesMetaData}
                                    onValidationChange={setCanSaveMinutes}
                                    initialFormValues={initialFormValues}
                                    editMode={editMode}
                                    onSave={handleSave}
                                    showSummaryStatus={showSummaryStatus}
                                    onCommitteeChange={setSelectedCommitteeId}
                                />
                            </TabPanel>
                            <TabPanel value={activeTab} index={1}>
                                <MinuteManagerAttendees
                                    onRegisterGetData={handleRegisterGetData}
                                    key={`attendees-${editMode}-${minutesItem?.id || 'new'}`}
                                    currentMinuteItem={attendeesMinutesItem}
                                    apiAttendees={
                                        editModalData?.attendees || minutesItem?.attendees || []
                                    }
                                    readonlyForCreate={
                                        editMode === MINUTES_EDIT_OPTIONS.CREATE &&
                                        !selectedCommitteeId
                                    }
                                    editMode={editMode}
                                    editModalInvitedAttendees={editModalData?.attending}
                                    editModalPresentAttendees={editModalData?.present}
                                    editModalApologiesAttendees={editModalData?.apologies}
                                    isLoading={editMinutesDetails}
                                />

                                <div style={{ marginTop: '20px' }}>
                                    <InputLabelStyled aria-label={i18n.t('ATTENDANCE_NOTES')}>
                                        {i18n.t('ATTENDANCE_NOTES')}
                                    </InputLabelStyled>
                                </div>
                                <ClassNames>
                                    {({ css }) => (
                                        <NewDialogTextField
                                            variant="outlined"
                                            value={attendanceNotes}
                                            onChange={onAttendanceNotesChange}
                                            onFocus={() => trackAttendanceNotesOnce('focus')}
                                            multiline
                                            placeholder={i18n.t('ATTENDANCE_NOTES_PLACEHOLDER')}
                                            className={css`
                                                ${TextFieldStyles}
                                            `}
                                            inputProps={{ maxLength: 3000 }}
                                            helperText={
                                                <TextLengthCounter
                                                    value={attendanceNotes}
                                                    maxValue={3000}
                                                    colour={true}
                                                />
                                            }
                                        />
                                    )}
                                </ClassNames>
                            </TabPanel>
                        </div>
                    )}
                </ClassNames>
            </DialogContent>
        </AtlasModal>
    )
}

interface TabPanelProps {
    children?: React.ReactNode
    index: number
    value: number
}

const TabPanel: FC<TabPanelProps> = ({ children, value, index }) => {
    return (
        <div
            role="tabpanel"
            id={`tabpanel-${index}`}
            aria-labelledby={`tab-${index}`}
            aria-hidden={value !== index}
            style={{
                display: value === index ? 'block' : 'none',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                width: '100%',
                minHeight: '400px',
                zIndex: value === index ? 1 : 0,
                pointerEvents: value === index ? 'auto' : 'none'
            }}>
            <Box p={0}>{children}</Box>
        </div>
    )
}

const getOkButtonLabel = (editMode: EditMode): string => {
    if (editMode === MINUTES_EDIT_OPTIONS.CREATE) return i18n.t('CREATE')
    if (editMode === MINUTES_EDIT_OPTIONS.EDIT) return i18n.t('SAVE')
    if (editMode === MINUTES_EDIT_OPTIONS.DUPLICATE) return i18n.t('DUPLICATE')

    return i18n.t('SAVE')
}

const tabPanelContainer = css`
    position: relative;
    min-height: 400px;
`

const btnCancel = css`
    color: ${AtlasActionPrimary};
    height: 40px;
    span {
        font-family: 'Source Sans Pro';
        font-weight: 600;
        font-size: 16px;
        text-transform: capitalize;
    }
    :focus {
        box-shadow: 0px 0px 0px 1px #ffffff, 0px 0px 0px 3px ${ColorBaseSkyBlue};
        border-radius: 2px;
    }
`
export const btnOk = css`
    height: 40px;
    span {
        font-family: 'Source Sans Pro';
        font-weight: 600;
        font-size: 16px;
        text-transform: capitalize;
        display: flex;
        justify-content: center;
    }
    :focus {
        box-shadow: 0px 0px 0px 1px #ffffff, 0px 0px 0px 3px ${ColorBaseSkyBlue};
        border-radius: 2px;
    }
`
